// FormBuilder.jsx - Editor Visual de Formularios
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FaPlus, FaEdit, FaTrash, FaCopy, FaSave, FaEye, FaGripVertical, FaTimes, FaCheck } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import TemplateField from './TemplateFieldRenderer';

// Frecuencia de aparición del campo en el formulario de actualización
// Fit Evolution (ticket #265569). `every_cycle` = cada 4 semanas (default,
// incluye plantillas viejas sin el atributo). `12_week` = solo cuando se
// cumple el ciclo de 12 semanas (status.is_12week_cycle).
const CADENCE_OPTIONS = [
  { id: 'every_cycle', name: 'Cada 4 semanas (todos los ciclos)' },
  { id: '12_week', name: 'Solo al cumplir 12 semanas' },
];

const API = process.env.REACT_APP_BACKEND_URL;

// Tipos de campos disponibles - Básicos y Avanzados
const FIELD_TYPES = [
  // Campos Básicos
  { id: 'text', name: 'Texto corto', icon: '📝', category: 'basic' },
  { id: 'textarea', name: 'Texto largo', icon: '📄', category: 'basic' },
  { id: 'number', name: 'Número', icon: '🔢', category: 'basic' },
  { id: 'email', name: 'Email', icon: '✉️', category: 'basic' },
  { id: 'select', name: 'Desplegable', icon: '📋', category: 'basic' },
  { id: 'multiselect', name: 'Selección múltiple', icon: '☑️', category: 'basic' },
  { id: 'radio', name: 'Opción única', icon: '🔘', category: 'basic' },
  { id: 'checkbox', name: 'Casillas', icon: '✅', category: 'basic' },
  // Campos Avanzados
  { id: 'date', name: 'Fecha', icon: '📅', category: 'advanced' },
  { id: 'file', name: 'Subir archivo', icon: '📎', category: 'advanced' },
  { id: 'slider', name: 'Slider numérico', icon: '🎚️', category: 'advanced' },
  { id: 'scale', name: 'Escala (1-10)', icon: '⭐', category: 'advanced' },
  { id: 'time', name: 'Hora', icon: '🕐', category: 'advanced' },
  { id: 'phone', name: 'Teléfono', icon: '📱', category: 'advanced' },
];

// Colores para secciones
const SECTION_COLORS = [
  { id: 'pink', name: 'Rosa', bg: 'rgba(var(--brand-primary-glow), 0.1)', border: 'rgba(var(--brand-primary-glow), 0.3)', text: 'text-pink-400' },
  { id: 'cyan', name: 'Cyan', bg: 'rgba(0, 240, 255, 0.1)', border: 'rgba(0, 240, 255, 0.3)', text: 'text-cyan-400' },
  { id: 'orange', name: 'Naranja', bg: 'rgba(255, 150, 0, 0.1)', border: 'rgba(255, 150, 0, 0.3)', text: 'text-orange-400' },
  { id: 'green', name: 'Verde', bg: 'rgba(34, 197, 94, 0.1)', border: 'rgba(34, 197, 94, 0.3)', text: 'text-green-400' },
  { id: 'purple', name: 'Morado', bg: 'rgba(168, 85, 247, 0.1)', border: 'rgba(168, 85, 247, 0.3)', text: 'text-purple-400' },
];

// Planes disponibles
const PLAN_OPTIONS = [
  { id: 'basic', name: 'Fit Evolution', color: 'bg-blue-500' },
  { id: 'premium', name: 'Plan Premium', color: 'bg-purple-500' },
  { id: 'vip_gold', name: 'VIP Gold', color: 'bg-yellow-500' },
  { id: 'competitivo', name: 'Competitivo', color: 'bg-red-500' },
];

const FormBuilder = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estado del formulario siendo editado
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    form_type: 'workout',
    target_plans: [],
    sections: [],
    fields: [],
    is_active: true
  });

  // Estados para edición de campos
  const [editingField, setEditingField] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [showSectionDialog, setShowSectionDialog] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/form-templates`, { withCredentials: false });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      form_type: 'workout',
      target_plans: [],
      sections: [],
      fields: [],
      is_active: true
    });
    setShowEditor(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      form_type: template.form_type || 'workout',
      target_plans: template.target_plans || [],
      sections: template.sections || [],
      fields: template.fields || [],
      is_active: template.is_active ?? true
    });
    setShowEditor(true);
  };

  const handleSaveTemplate = async () => {
    if (!formData.name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (formData.target_plans.length === 0) {
      toast.error('Selecciona al menos un plan');
      return;
    }

    setSaving(true);
    try {
      if (selectedTemplate) {
        await axios.put(`${API}/api/admin/form-templates/${selectedTemplate.id}`, formData, { withCredentials: false });
        toast.success('Plantilla actualizada');
      } else {
        await axios.post(`${API}/api/admin/form-templates`, formData, { withCredentials: false });
        toast.success('Plantilla creada');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error al guardar plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await axios.delete(`${API}/api/admin/form-templates/${templateId}`, { withCredentials: false });
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/api/admin/form-templates/${templateId}/duplicate`, {}, { withCredentials: false });
      toast.success('Plantilla duplicada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al duplicar');
    }
  };

  // ===== Secciones =====
  const handleAddSection = () => {
    setEditingSection({
      id: Date.now().toString(),
      title: '',
      description: '',
      icon: '📝',
      color: 'pink',
      order: formData.sections.length
    });
    setShowSectionDialog(true);
  };

  const handleSaveSection = () => {
    if (!editingSection.title) {
      toast.error('El título es requerido');
      return;
    }
    
    const existingIndex = formData.sections.findIndex(s => s.id === editingSection.id);
    if (existingIndex >= 0) {
      const updated = [...formData.sections];
      updated[existingIndex] = editingSection;
      setFormData({ ...formData, sections: updated });
    } else {
      setFormData({ ...formData, sections: [...formData.sections, editingSection] });
    }
    setShowSectionDialog(false);
    setEditingSection(null);
  };

  const handleDeleteSection = (sectionId) => {
    // También eliminar campos de esa sección
    setFormData({
      ...formData,
      sections: formData.sections.filter(s => s.id !== sectionId),
      fields: formData.fields.filter(f => f.section !== sectionId)
    });
  };

  // ===== Campos =====
  const handleAddField = (sectionId = null) => {
    setEditingField({
      id: Date.now().toString(),
      type: 'text',
      label: '',
      placeholder: '',
      required: false,
      options: [],
      order: formData.fields.filter(f => f.section === sectionId).length,
      section: sectionId,
      helpText: '',
      // Campos avanzados
      min: 0,
      max: 100,
      step: 1,
      scaleMin: 1,
      scaleMax: 10,
      scaleLabels: { low: 'Bajo', high: 'Alto' },
      acceptedFileTypes: '.pdf,.jpg,.png,.jpeg',
      maxFileSize: 5, // MB
      // Campo para actualizaciones
      is_mandatory_on_update: false,
      // Frecuencia de aparición (ticket #265569)
      cadence: 'every_cycle'
    });
    setShowFieldDialog(true);
  };

  const handleSaveField = () => {
    if (!editingField.label) {
      toast.error('La etiqueta es requerida');
      return;
    }
    
    const existingIndex = formData.fields.findIndex(f => f.id === editingField.id);
    if (existingIndex >= 0) {
      const updated = [...formData.fields];
      updated[existingIndex] = editingField;
      setFormData({ ...formData, fields: updated });
    } else {
      setFormData({ ...formData, fields: [...formData.fields, editingField] });
    }
    setShowFieldDialog(false);
    setEditingField(null);
  };

  const handleDeleteField = (fieldId) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter(f => f.id !== fieldId)
    });
  };

  const handleAddOption = () => {
    const newOption = {
      id: Date.now().toString(),
      label: '',
      value: ''
    };
    setEditingField({
      ...editingField,
      options: [...(editingField.options || []), newOption]
    });
  };

  const handleUpdateOption = (index, key, value) => {
    const options = [...editingField.options];
    options[index] = { ...options[index], [key]: value };
    if (key === 'label' && !options[index].value) {
      options[index].value = value.toLowerCase().replace(/\s+/g, '_');
    }
    setEditingField({ ...editingField, options });
  };

  const handleRemoveOption = (index) => {
    const options = editingField.options.filter((_, i) => i !== index);
    setEditingField({ ...editingField, options });
  };

  // Obtener campos de una sección
  const getFieldsForSection = (sectionId) => {
    return formData.fields.filter(f => f.section === sectionId).sort((a, b) => a.order - b.order);
  };

  // Campos sin sección
  const getFieldsWithoutSection = () => {
    return formData.fields.filter(f => !f.section).sort((a, b) => a.order - b.order);
  };

  // ===== Render =====
  if (showEditor) {
    return (
      <div className="space-y-6">
        {/* Header del Editor */}
        <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  ✏️ {selectedTemplate ? 'Editar Formulario' : 'Nuevo Formulario'}
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Configura los campos y secciones del formulario
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPreview(true)} className="border-cyan-500 text-cyan-400">
                  <FaEye className="mr-2" /> Vista Previa
                </Button>
                <Button variant="outline" onClick={() => setShowEditor(false)} className="border-gray-500 text-gray-400">
                  <FaTimes className="mr-2" /> Cancelar
                </Button>
                <Button onClick={handleSaveTemplate} disabled={saving} className="bg-pink-600 hover:bg-pink-700">
                  <FaSave className="mr-2" /> {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuración General */}
          <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardHeader>
              <CardTitle className="text-lg text-white">⚙️ Configuración</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Nombre del Formulario *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Formulario de Rutina Basic"
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del formulario..."
                  className="bg-white/10 border-white/20 text-white"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-gray-300">Tipo de Formulario *</Label>
                <Select value={formData.form_type} onValueChange={(val) => setFormData({ ...formData, form_type: val })}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workout">🏋️ Rutina de Entrenamiento</SelectItem>
                    <SelectItem value="diet">🥗 Plan de Nutrición</SelectItem>
                    <SelectItem value="fit_evolution_initial">🚀 Fit Evolution · Inicial</SelectItem>
                    <SelectItem value="fit_evolution_update">🔄 Fit Evolution · Actualización</SelectItem>
                    <SelectItem value="asesoria_initial">👑 Asesoría 1 a 1 · Inicial</SelectItem>
                    <SelectItem value="asesoria_update">🎯 Asesoría 1 a 1 · Actualización</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-gray-300">¿Para qué planes? *</Label>
                <div className="space-y-2 mt-2">
                  {PLAN_OPTIONS.map(plan => (
                    <label key={plan.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.target_plans.includes(plan.id)}
                        onChange={(e) => {
                          const plans = e.target.checked
                            ? [...formData.target_plans, plan.id]
                            : formData.target_plans.filter(p => p !== plan.id);
                          setFormData({ ...formData, target_plans: plans });
                        }}
                        className="w-4 h-4"
                      />
                      <span className={`w-3 h-3 rounded-full ${plan.color}`}></span>
                      <span className="text-gray-300">{plan.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label className="text-gray-300">Formulario activo</Label>
              </div>
            </CardContent>
          </Card>

          {/* Secciones y Campos */}
          <div className="lg:col-span-2 space-y-4">
            {/* Info compacta sobre preguntas base */}
            <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
              <span className="text-amber-400 text-lg">💡</span>
              <p className="text-amber-300 text-sm">
                <strong>Nota:</strong> El formulario base ya incluye: datos personales, objetivos, alimentación, salud y entrenamiento. 
                Aquí puedes agregar <strong>preguntas adicionales</strong> específicas para este plan.
              </p>
            </div>
            
            {/* Botón agregar sección */}
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">📋 Preguntas Adicionales</h3>
              <Button onClick={handleAddSection} className="bg-cyan-600 hover:bg-cyan-700">
                <FaPlus className="mr-2" /> Nueva Sección
              </Button>
            </div>

            {/* Campos sin sección */}
            {getFieldsWithoutSection().length > 0 && (
              <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <CardHeader className="py-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm text-gray-400">Campos Generales</CardTitle>
                    <Button size="sm" variant="ghost" onClick={() => handleAddField(null)} className="text-pink-400">
                      <FaPlus className="mr-1" /> Campo
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {getFieldsWithoutSection().map(field => (
                    <FieldItem 
                      key={field.id} 
                      field={field} 
                      onEdit={() => { setEditingField(field); setShowFieldDialog(true); }}
                      onDelete={() => handleDeleteField(field.id)}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Secciones */}
            {formData.sections.sort((a, b) => a.order - b.order).map(section => {
              const colorConfig = SECTION_COLORS.find(c => c.id === section.color) || SECTION_COLORS[0];
              return (
                <Card 
                  key={section.id} 
                  style={{ backgroundColor: colorConfig.bg, border: `1px solid ${colorConfig.border}` }}
                >
                  <CardHeader className="py-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{section.icon}</span>
                        <div>
                          <CardTitle className={`text-lg ${colorConfig.text}`}>{section.title}</CardTitle>
                          {section.description && (
                            <CardDescription className="text-gray-400">{section.description}</CardDescription>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleAddField(section.id)} className="text-green-400">
                          <FaPlus />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingSection(section); setShowSectionDialog(true); }} className="text-blue-400">
                          <FaEdit />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteSection(section.id)} className="text-red-400">
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {getFieldsForSection(section.id).map(field => (
                      <FieldItem 
                        key={field.id} 
                        field={field} 
                        onEdit={() => { setEditingField(field); setShowFieldDialog(true); }}
                        onDelete={() => handleDeleteField(field.id)}
                      />
                    ))}
                    {getFieldsForSection(section.id).length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-4">
                        Sin campos. Haz clic en + para agregar.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {formData.sections.length === 0 && getFieldsWithoutSection().length === 0 && (
              <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.2)' }}>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-400 mb-4">No hay secciones ni campos</p>
                  <Button onClick={handleAddSection} className="bg-cyan-600">
                    <FaPlus className="mr-2" /> Crear Primera Sección
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Dialog para Sección */}
        <Dialog open={showSectionDialog} onOpenChange={setShowSectionDialog}>
          <DialogContent style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
            <DialogHeader>
              <DialogTitle className="text-white">{editingSection?.id ? 'Editar' : 'Nueva'} Sección</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-300">Título *</Label>
                <Input
                  value={editingSection?.title || ''}
                  onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Descripción</Label>
                <Input
                  value={editingSection?.description || ''}
                  onChange={(e) => setEditingSection({ ...editingSection, description: e.target.value })}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Icono</Label>
                <Input
                  value={editingSection?.icon || '📝'}
                  onChange={(e) => setEditingSection({ ...editingSection, icon: e.target.value })}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="Emoji..."
                />
              </div>
              <div>
                <Label className="text-gray-300">Color</Label>
                <div className="flex gap-2 mt-2">
                  {SECTION_COLORS.map(color => (
                    <button
                      key={color.id}
                      onClick={() => setEditingSection({ ...editingSection, color: color.id })}
                      className={`w-8 h-8 rounded-full border-2 ${editingSection?.color === color.id ? 'border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: color.border }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSectionDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveSection} className="bg-pink-600">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para Campo */}
        <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
            <DialogHeader>
              <DialogTitle className="text-white">{editingField?.id ? 'Editar' : 'Nuevo'} Campo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Tipo de Campo *</Label>
                  <Select value={editingField?.type || 'text'} onValueChange={(val) => setEditingField({ ...editingField, type: val })}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs text-gray-400 font-semibold">📦 Campos Básicos</div>
                      {FIELD_TYPES.filter(t => t.category === 'basic').map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.icon} {type.name}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1 text-xs text-gray-400 font-semibold mt-2">⚡ Campos Avanzados</div>
                      {FIELD_TYPES.filter(t => t.category === 'advanced').map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.icon} {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Etiqueta *</Label>
                  <Input
                    value={editingField?.label || ''}
                    onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="Ej: ¿Cuál es tu objetivo?"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Placeholder</Label>
                <Input
                  value={editingField?.placeholder || ''}
                  onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="Texto de ayuda dentro del campo..."
                />
              </div>

              <div>
                <Label className="text-gray-300">Texto de Ayuda</Label>
                <Input
                  value={editingField?.helpText || ''}
                  onChange={(e) => setEditingField({ ...editingField, helpText: e.target.value })}
                  className="bg-white/10 border-white/20 text-white"
                  placeholder="Información adicional para el usuario..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingField?.required || false}
                  onChange={(e) => setEditingField({ ...editingField, required: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label className="text-gray-300">Campo requerido</Label>
              </div>

              <div className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
                <input
                  type="checkbox"
                  checked={editingField?.is_mandatory_on_update || false}
                  onChange={(e) => setEditingField({ ...editingField, is_mandatory_on_update: e.target.checked })}
                  className="w-4 h-4 accent-purple-500"
                />
                <div>
                  <Label className="text-purple-300">Obligatorio en actualización</Label>
                  <p className="text-purple-400/60 text-xs mt-0.5">
                    Si está marcado, los usuarios existentes DEBEN responder esta pregunta cuando actualicen su formulario
                  </p>
                </div>
              </div>

              <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
                <Label className="text-emerald-300">Frecuencia de aparición (formulario de actualización)</Label>
                <p className="text-emerald-400/60 text-xs mt-0.5 mb-2">
                  Controla si esta pregunta aparece cada 4 semanas o solo cuando la clienta cumple su ciclo de 12 semanas.
                </p>
                <Select value={editingField?.cadence || 'every_cycle'} onValueChange={(val) => setEditingField({ ...editingField, cadence: val })}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CADENCE_OPTIONS.map(opt => (
                      <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Opciones para campos de selección */}
              {['select', 'multiselect', 'radio', 'checkbox'].includes(editingField?.type) && (
                <div className="space-y-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <div className="flex justify-between items-center">
                    <Label className="text-gray-300">Opciones</Label>
                    <Button size="sm" onClick={handleAddOption} className="bg-green-600">
                      <FaPlus className="mr-1" /> Agregar Opción
                    </Button>
                  </div>
                  {editingField?.options?.map((option, index) => (
                    <div key={option.id} className="flex gap-2 items-center">
                      <Input
                        value={option.label}
                        onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                        placeholder="Etiqueta visible"
                        className="bg-white/10 border-white/20 text-white flex-1"
                      />
                      <Input
                        value={option.value}
                        onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                        placeholder="Valor interno"
                        className="bg-white/10 border-white/20 text-white w-32"
                      />
                      <Button size="sm" variant="ghost" onClick={() => handleRemoveOption(index)} className="text-red-400">
                        <FaTimes />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Configuración para Slider */}
              {editingField?.type === 'slider' && (
                <div className="space-y-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <Label className="text-gray-300">🎚️ Configuración del Slider</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-400 text-sm">Mínimo</Label>
                      <Input
                        type="number"
                        value={editingField?.min ?? 0}
                        onChange={(e) => setEditingField({ ...editingField, min: parseInt(e.target.value) || 0 })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Máximo</Label>
                      <Input
                        type="number"
                        value={editingField?.max ?? 100}
                        onChange={(e) => setEditingField({ ...editingField, max: parseInt(e.target.value) || 100 })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Paso</Label>
                      <Input
                        type="number"
                        value={editingField?.step ?? 1}
                        onChange={(e) => setEditingField({ ...editingField, step: parseInt(e.target.value) || 1 })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Configuración para Escala */}
              {editingField?.type === 'scale' && (
                <div className="space-y-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <Label className="text-gray-300">⭐ Configuración de la Escala</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-400 text-sm">Valor mínimo</Label>
                      <Input
                        type="number"
                        value={editingField?.scaleMin ?? 1}
                        onChange={(e) => setEditingField({ ...editingField, scaleMin: parseInt(e.target.value) || 1 })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Valor máximo</Label>
                      <Input
                        type="number"
                        value={editingField?.scaleMax ?? 10}
                        onChange={(e) => setEditingField({ ...editingField, scaleMax: parseInt(e.target.value) || 10 })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-400 text-sm">Etiqueta mínimo</Label>
                      <Input
                        value={editingField?.scaleLabels?.low || 'Bajo'}
                        onChange={(e) => setEditingField({ ...editingField, scaleLabels: { ...editingField.scaleLabels, low: e.target.value } })}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Ej: Bajo, Nada..."
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Etiqueta máximo</Label>
                      <Input
                        value={editingField?.scaleLabels?.high || 'Alto'}
                        onChange={(e) => setEditingField({ ...editingField, scaleLabels: { ...editingField.scaleLabels, high: e.target.value } })}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Ej: Alto, Mucho..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Configuración para File Upload */}
              {editingField?.type === 'file' && (
                <div className="space-y-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  <Label className="text-gray-300">📎 Configuración de Archivo</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-400 text-sm">Tipos aceptados</Label>
                      <Input
                        value={editingField?.acceptedFileTypes || '.pdf,.jpg,.png,.jpeg'}
                        onChange={(e) => setEditingField({ ...editingField, acceptedFileTypes: e.target.value })}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder=".pdf,.jpg,.png"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 text-sm">Tamaño máx. (MB)</Label>
                      <Input
                        type="number"
                        value={editingField?.maxFileSize ?? 5}
                        onChange={(e) => setEditingField({ ...editingField, maxFileSize: parseInt(e.target.value) || 5 })}
                        className="bg-white/10 border-white/20 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFieldDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveField} className="bg-pink-600">Guardar Campo</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" style={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)' }}>
            <DialogHeader>
              <DialogTitle className="text-white">👁️ Vista Previa: {formData.name || 'Sin nombre'}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <FormPreview formData={formData} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Lista de plantillas
  return (
    <div className="space-y-6">
      <Card style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-white">📝 Editor de Formularios</CardTitle>
              <CardDescription className="text-gray-400">
                Crea y personaliza los formularios para cada tipo de plan
              </CardDescription>
            </div>
            <Button onClick={handleCreateNew} className="bg-pink-600 hover:bg-pink-700">
              <FaPlus className="mr-2" /> Nuevo Formulario
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Cargando...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 mb-4">No hay formularios personalizados</p>
              <p className="text-gray-500 text-sm mb-4">Los formularios por defecto están activos</p>
              <Button onClick={handleCreateNew} className="bg-cyan-600">
                <FaPlus className="mr-2" /> Crear Primer Formulario
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Ordenar por plan: basic, premium, vip_gold, competitivo */}
              {['basic', 'premium', 'vip_gold', 'competitivo'].map(planId => {
                const planTemplates = templates.filter(t => t.target_plans?.includes(planId));
                if (planTemplates.length === 0) return null;
                
                const planName = PLAN_OPTIONS.find(p => p.id === planId)?.name || planId;
                const planColor = PLAN_OPTIONS.find(p => p.id === planId)?.color || 'bg-gray-500';
                
                // Ordenar: dieta primero, luego rutina
                const sortedTemplates = [...planTemplates].sort((a, b) => {
                  if (a.form_type === 'diet' && b.form_type === 'workout') return -1;
                  if (a.form_type === 'workout' && b.form_type === 'diet') return 1;
                  return 0;
                });
                
                return (
                  <div key={planId}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-3 h-3 rounded-full ${planColor}`}></div>
                      <h3 className="text-lg font-semibold text-white">{planName}</h3>
                      <span className="text-gray-500 text-sm">({sortedTemplates.length} formularios)</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {sortedTemplates.map(template => (
                <Card key={template.id} style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-white">{template.name}</h4>
                        <p className="text-sm text-gray-400">{template.description}</p>
                      </div>
                      <Badge className={template.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                        {template.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    
                    <div className="flex gap-2 mb-3">
                      <Badge variant="outline" className="border-pink-500 text-pink-400">
                        {template.form_type === 'workout'
                          ? '🏋️ Rutina'
                          : template.form_type === 'diet'
                          ? '🥗 Dieta'
                          : template.form_type === 'fit_evolution_initial'
                          ? '🚀 Fit Evolution · Inicial'
                          : template.form_type === 'fit_evolution_update'
                          ? '🔄 Fit Evolution · Actualización'
                          : template.form_type === 'asesoria_initial'
                          ? '👑 Asesoría 1 a 1 · Inicial'
                          : template.form_type === 'asesoria_update'
                          ? '🎯 Asesoría 1 a 1 · Actualización'
                          : template.form_type}
                      </Badge>
                      {template.target_plans?.map(plan => (
                        <Badge key={plan} variant="outline" className="text-xs">
                          {PLAN_OPTIONS.find(p => p.id === plan)?.name || plan}
                        </Badge>
                      ))}
                    </div>

                    <div className="text-xs text-gray-500 mb-3">
                      {template.sections?.length || 0} secciones • {template.fields?.length || 0} campos
                    </div>

                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEditTemplate(template)} className="flex-1 border-blue-500 text-blue-400">
                        <FaEdit className="mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDuplicateTemplate(template.id)} className="border-cyan-500 text-cyan-400">
                        <FaCopy />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteTemplate(template.id)} className="border-red-500 text-red-400">
                        <FaTrash />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente para mostrar un campo en el editor
const FieldItem = ({ field, onEdit, onDelete }) => {
  const fieldType = FIELD_TYPES.find(t => t.id === field.type) || FIELD_TYPES[0];
  return (
    <div className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/10">
      <div className="flex items-center gap-2">
        <FaGripVertical className="text-gray-500 cursor-move" />
        <span>{fieldType.icon}</span>
        <span className="text-white">{field.label}</span>
        {field.required && <span className="text-red-400">*</span>}
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={onEdit} className="text-blue-400 hover:text-blue-300">
          <FaEdit />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-400 hover:text-red-300">
          <FaTrash />
        </Button>
      </div>
    </div>
  );
};

// Componente de vista previa
const FormPreview = ({ formData }) => {
  return (
    <div className="space-y-6">
      {formData.sections.map(section => {
        const colorConfig = SECTION_COLORS.find(c => c.id === section.color) || SECTION_COLORS[0];
        const sectionFields = formData.fields.filter(f => f.section === section.id);
        
        return (
          <div 
            key={section.id} 
            className="p-4 rounded-lg"
            style={{ backgroundColor: colorConfig.bg, border: `1px solid ${colorConfig.border}` }}
          >
            <h3 className={`font-semibold ${colorConfig.text} mb-4 flex items-center gap-2`}>
              {section.icon} {section.title}
            </h3>
            {section.description && (
              <p className="text-gray-400 text-sm mb-4">{section.description}</p>
            )}
            <div className="space-y-4">
              {sectionFields.map(field => (
                <PreviewField key={field.id} field={field} />
              ))}
            </div>
          </div>
        );
      })}
      
      {/* Campos sin sección */}
      {formData.fields.filter(f => !f.section).map(field => (
        <PreviewField key={field.id} field={field} />
      ))}
    </div>
  );
};

// Campo de vista previa - delega en el mismo type-switch que usa el
// formulario real del cliente (TemplateFieldRenderer) para que admin y
// cliente siempre rendericen igual (ticket #265569).
const PreviewField = ({ field }) => (
  <TemplateField field={field} disabled />
);

export default FormBuilder;
